// src/components/YourModule/PurchaseOrderModule/AllPurchaseOrders.jsx

import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import { useReactToPrint } from "react-to-print";
import {
  Search,
  Plus,
  Trash2,
  Edit,
  ChevronDown,
  ChevronRight,
  FileText,
  AlertCircle,
  Package,
  Eye,
  Printer,
  Archive,
  Clock,
  MapPin,
  Calendar,
  User,
  CreditCard,
  CheckCircle,
  XCircle,
  Info,
  RotateCcw,
  History,
  ShieldAlert,
  RefreshCw,
  HelpCircle,
} from "lucide-react";
import POReceiptsModal from "./POReceiptsModal";
import Loader from "../../../loader/Loader";
import AddPurchaseOrder from "./AddPurchaseOrder";
import Pagination from "../../Pagination";
import RejectItemModal from "./RejectItemModal";
import { domain, domainAccounting } from "../../../../security";
import ViewRejectionModal from "./ViewRejectionModal";
import PrintPurchaseOrder from "./PrintPurchaseOrder";
import POApprovalActions from "./POApprovalActions";
import { selectUserID, selectLocationId } from "../../../../redux/IchthusSlice";

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

const calculateOriginalTotal = (po) => {
  if (!po || !po.purchaseOrderLineItems) return 0;
  const originalSubtotal = po.purchaseOrderLineItems.reduce((acc, item) => {
    const qty = item.orderedQuantity || item.quantity || 0;
    return acc + qty * (item.unitPrice || 0);
  }, 0);
  let originalEwt = 0;
  if (po.ewt > 0) {
    const taxBase = po.vat > 0 ? originalSubtotal / 1.12 : originalSubtotal;
    originalEwt = taxBase * 0.01;
  }
  return originalSubtotal + (po.shippingCost || 0) - originalEwt;
};

const formatCurrency = (amount, currency = "PHP") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "PHP",
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// ─────────────────────────────────────────────────────────────
// SHARED COMPONENTS
// ─────────────────────────────────────────────────────────────

const PriceDisplay = ({ currentTotal, originalTotal, currency }) => {
  const hasChanged = Math.abs(currentTotal - originalTotal) > 0.1;
  return (
    <div className="flex flex-col items-end">
      <span className="font-bold text-gray-900">
        {formatCurrency(currentTotal, currency)}
      </span>
      {hasChanged && (
        <span className="text-xs text-gray-400 line-through">
          {formatCurrency(originalTotal, currency)}
        </span>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const getStatusConfig = (status) => {
    const s = status?.toLowerCase() || "";
    if (s === "closed")
      return {
        color: "bg-emerald-50 text-emerald-700 border-emerald-200",
        icon: <CheckCircle size={11} />,
      };
    if (s === "open")
      return {
        color: "bg-blue-50 text-blue-700 border-blue-200",
        icon: <Info size={11} />,
      };
    if (s.includes("partially"))
      return {
        color: "bg-orange-50 text-orange-700 border-orange-200",
        icon: <Package size={11} />,
      };
    if (s.includes("draft"))
      return {
        color: "bg-slate-100 text-slate-600 border-slate-200",
        icon: <FileText size={11} />,
      };
    if (s === "pending")
      return {
        color: "bg-amber-50 text-amber-700 border-amber-200",
        icon: <Clock size={11} />,
      };
    if (s === "rejected")
      return {
        color: "bg-red-50 text-red-700 border-red-200",
        icon: <XCircle size={11} />,
      };
    return {
      color: "bg-gray-50 text-gray-600 border-gray-200",
      icon: <Info size={11} />,
    };
  };
  const config = getStatusConfig(status);
  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}
    >
      {config.icon}
      <span>{status}</span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MANUAL SYNC MODAL
// ─────────────────────────────────────────────────────────────

const ManualSyncModal = ({ isOpen, onClose, onSync, isSyncing }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }
    onSync(startDate, endDate);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw size={18} className="text-emerald-600" />
            <h2 className="text-base font-bold text-slate-800">
              Manual Sync to Accounting
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isSyncing}
            className="text-slate-400 hover:text-slate-700 transition"
          >
            <XCircle size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-blue-50 text-blue-800 p-3 rounded-lg text-sm flex items-start gap-2 border border-blue-100 mb-2">
            <Info size={18} className="shrink-0 mt-0.5 text-blue-600" />
            <p>
              <strong>
                Do you want to sync manually in the accounting or did you not
                see the current upload to the accounting?
              </strong>
              <br />
              <br />
              Select a date range below to check for any missed Approved/Live
              orders and push them.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                required
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isSyncing}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                required
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isSyncing}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-sm"
              />
            </div>
          </div>
          <div className="pt-4 flex justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isSyncing}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSyncing}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition disabled:opacity-70"
            >
              {isSyncing ? (
                <>
                  <RefreshCw size={14} className="animate-spin" /> Syncing...
                </>
              ) : (
                "Start Sync"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// HISTORY MODAL
// ─────────────────────────────────────────────────────────────

const POHistoryModal = ({ isOpen, onClose, currentUserId, onRestored }) => {
  const [liveHistory, setLiveHistory] = useState([]);
  const [stagingHistory, setStagingHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [historyTab, setHistoryTab] = useState("live");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  const fetchHistory = useCallback(async () => {
    if (!isOpen) return;
    try {
      setLoading(true);
      const [liveRes, stagingRes] = await Promise.all([
        axios.get(
          `${domain}/api/PurchaseOrderHeaders/history?search=${search}`,
        ),
        axios.get(
          `${domain}/api/PurchaseOrderStaging/history?search=${search}`,
        ),
      ]);
      setLiveHistory(liveRes.data || []);
      setStagingHistory(stagingRes.data || []);
    } catch {
      toast.error("Failed to load PO history.");
    } finally {
      setLoading(false);
    }
  }, [isOpen, search]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  const handleRestore = async (id, isStaging) => {
    try {
      const endpoint = isStaging
        ? `${domain}/api/PurchaseOrderStaging/restore/${id}`
        : `${domain}/api/PurchaseOrderHeaders/restore/${id}`;
      await axios.put(
        endpoint,
        { userID: currentUserId },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Purchase Order restored successfully!");
      fetchHistory();
      onRestored();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to restore.");
    }
  };

  if (!isOpen) return null;

  const activeData = historyTab === "live" ? liveHistory : stagingHistory;
  const filtered = activeData.filter((p) => {
    const vendor = p.vendorName || "";
    const poNum = p.poNumber || `TMP-${p.id}`;
    const q = search.toLowerCase();
    return vendor.toLowerCase().includes(q) || poNum.toLowerCase().includes(q);
  });
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filtered.slice(indexOfFirst, indexOfLast);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-xl">
              <History size={18} className="text-slate-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800">
                Archived Purchase Orders
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Soft-deleted records — restore to bring them back
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition"
          >
            <XCircle size={20} />
          </button>
        </div>

        <div className="px-6 pt-4 pb-3 border-b border-slate-100 shrink-0 space-y-3">
          <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit">
            {[
              { key: "live", label: `Live POs (${liveHistory.length})` },
              { key: "drafts", label: `Drafts (${stagingHistory.length})` },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => {
                  setHistoryTab(t.key);
                  setCurrentPage(1);
                }}
                className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                  historyTab === t.key
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by PO number or vendor..."
              className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : currentItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                <Archive size={22} className="text-slate-400" />
              </div>
              <p className="text-slate-500 font-medium">
                No archived records found.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {currentItems.map((item) => {
                const isStaging = historyTab === "drafts";
                const displayNumber = isStaging
                  ? `TMP-${item.id}`
                  : item.poNumber;
                return (
                  <div
                    key={item.id}
                    className="bg-white rounded-xl border border-slate-200 shadow-sm p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-bold text-slate-800 text-sm">
                            {displayNumber}
                          </span>
                          <StatusBadge status={item.status} />
                        </div>
                        <p className="text-sm text-slate-500 font-medium truncate">
                          {item.vendorName || "Unknown Vendor"}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <span className="font-bold text-slate-800 text-sm">
                          {formatCurrency(item.grandTotal, item.currency)}
                        </span>
                        <button
                          onClick={() => handleRestore(item.id, isStaging)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-semibold hover:bg-emerald-100 transition"
                        >
                          <RotateCcw size={12} /> Restore
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="pt-2">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={filtered.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MOBILE CARD
// ─────────────────────────────────────────────────────────────

const MobilePOCard = ({
  po,
  expanded,
  onToggle,
  onEdit,
  onSoftDelete,
  onRejectItem,
  onPrint,
  currentUserId,
  refreshData,
  onApproveSuccess,
}) => {
  const originalTotal = calculateOriginalTotal(po);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 mb-3 overflow-hidden">
      <div className="p-4 cursor-pointer" onClick={() => onToggle(po.id)}>
        <div className="flex justify-between items-start gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="p-1.5 bg-blue-50 rounded-lg shrink-0">
              <FileText size={15} className="text-blue-600" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-gray-900 text-sm">
                  {po.poNumber}
                </h3>
                {po.isStaging && (
                  <span className="text-xs font-medium bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded-full">
                    DRAFT
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 truncate">
                {po.vendorDetails?.vendorName || po.vendor}
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <StatusBadge status={po.status} />
            <div className="mt-1.5">
              <PriceDisplay
                currentTotal={po.grandTotal}
                originalTotal={originalTotal}
                currency={po.currency}
              />
            </div>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-4">
          <div className="grid grid-cols-1 gap-1.5 text-xs text-gray-600 mb-4 bg-white p-3 rounded-lg border border-gray-100">
            <div className="flex items-center gap-2">
              <MapPin size={12} className="text-gray-400" />
              <span>
                Location: <strong>{po.location || "N/A"}</strong>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <User size={12} className="text-gray-400" />
              <span>
                Approver: <strong>{po.approverName || "N/A"}</strong>
              </span>
            </div>
          </div>

          <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-1.5 text-xs uppercase tracking-wide text-gray-500">
            <Package size={13} /> Order Items
          </h4>
          <div className="space-y-2 mb-4">
            {po.purchaseOrderLineItems.map((item) => (
              <div
                key={item.id}
                className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm"
              >
                <div className="flex justify-between mb-1">
                  <span className="font-medium text-gray-900 text-xs">
                    {item.productName}
                  </span>
                  <span className="font-bold text-xs">
                    {item.rejectedQuantity > 0 ? (
                      <span className="flex items-center gap-1">
                        <span>
                          {formatCurrency(
                            ((item.orderedQuantity || item.quantity) -
                              item.rejectedQuantity) *
                              item.unitPrice,
                            po.currency,
                          )}
                        </span>
                        <span className="text-red-400">
                          (-
                          {formatCurrency(
                            item.rejectedQuantity * item.unitPrice,
                            po.currency,
                          )}
                          )
                        </span>
                      </span>
                    ) : (
                      formatCurrency(item.lineTotal, po.currency)
                    )}
                  </span>
                </div>
                <div className="text-xs text-gray-400 flex gap-3">
                  <span>
                    Qty: {item.orderedQuantity || item.quantity}{" "}
                    {item.unitOfMeasure}
                  </span>
                  <span>@ {formatCurrency(item.unitPrice, po.currency)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
            <button
              onClick={() => onPrint(po)}
              className="flex-1 py-2 bg-white text-gray-600 rounded-lg text-xs font-medium border border-gray-200 hover:bg-gray-50 flex items-center justify-center gap-1.5"
            >
              <Printer size={14} /> Print
            </button>

            {po.isStaging ? (
              <div className="flex-1">
                <POApprovalActions
                  po={po}
                  currentUserId={currentUserId}
                  refreshData={refreshData}
                  onEdit={onEdit}
                  onDelete={onSoftDelete}
                  onApproveSuccess={() => onApproveSuccess(po)}
                />
              </div>
            ) : (
              <>
                {po.status !== "Closed" &&
                  !po.status?.toLowerCase().includes("partially") && (
                    <button
                      onClick={() => onEdit(po)}
                      className="flex-1 py-2 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium border border-blue-100 flex items-center justify-center gap-1.5"
                    >
                      <Edit size={13} /> Edit
                    </button>
                  )}
                {po.status !== "Closed" &&
                  !po.status?.toLowerCase().includes("partially") && (
                    <button
                      onClick={() => onSoftDelete(po.id, po.isStaging)}
                      className="flex-1 py-2 bg-red-50 text-red-700 rounded-lg text-xs font-medium border border-red-100 flex items-center justify-center gap-1.5"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────

const AllPurchaseOrders = () => {
  const currentUserId = useSelector(selectUserID);
  const userLocationId = useSelector(selectLocationId);

  const [poData, setPoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [poToEdit, setPoToEdit] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [expandedPO, setExpandedPO] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState("temp");
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isReceiptsModalOpen, setIsReceiptsModalOpen] = useState(false);
  const [selectedPoForReceipts, setSelectedPoForReceipts] = useState(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [itemToReject, setItemToReject] = useState({
    lineItem: null,
    headerId: null,
  });
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [itemToView, setItemToView] = useState(null);
  const [allLocationsList, setAllLocationsList] = useState([]);

  // Sync state
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const [printData, setPrintData] = useState(null);
  const printPORef = useRef();

  const locations = useMemo(() => {
    if (allLocationsList.length === 0) return ["All"];
    const locNames = allLocationsList.map((l) => l.locationName).sort();
    const uniqueNames = [...new Set(locNames)];
    return [...uniqueNames, "All"];
  }, [allLocationsList]);

  useEffect(() => {
    if (allLocationsList.length > 0 && selectedLocation === "") {
      if (userLocationId) {
        const userLoc = allLocationsList.find(
          (l) => l.id === parseInt(userLocationId),
        );
        if (userLoc) {
          setSelectedLocation(userLoc.locationName);
        } else {
          setSelectedLocation("All");
        }
      } else {
        setSelectedLocation("All");
      }
    }
  }, [allLocationsList, userLocationId, selectedLocation]);

  const handlePrintPO = useReactToPrint({
    content: () => printPORef.current,
    documentTitle: `PO_${printData?.poNumber || "Document"}`,
  });

  const triggerPrint = (po) => {
    setPrintData(po);
    setTimeout(() => handlePrintPO(), 300);
  };

  // ── Fetch Orders ─────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [liveResponse, stagingResponse, locResponse] = await Promise.all([
        axios.get(`${domain}/api/PurchaseOrderHeaders`),
        axios.get(`${domain}/api/PurchaseOrderStaging`),
        axios.get(`${domain}/api/Locations`),
      ]);

      setAllLocationsList(locResponse.data);
      const liveOrders = liveResponse.data.map((po) => ({
        ...po,
        isStaging: false,
        vendor: po.vendorDetails?.vendorName || po.vendor,
      }));
      const stagingOrders = stagingResponse.data.map((draft) => ({
        ...draft,
        poNumber: `TMP-${draft.id}`,
        isStaging: true,
        vendor: draft.vendorDetails?.vendorName || draft.vendor,
        purchaseOrderLineItems: draft.purchaseOrderLineItems || [],
      }));
      setPoData([...stagingOrders, ...liveOrders].sort((a, b) => b.id - a.id));
    } catch {
      toast.error("Failed to fetch purchase orders.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Sync Functions ─────────

  const pushSingleOrderToAccounting = async (po, approvedPoNumber) => {
    try {
      /* const currentSubtotal = (po.purchaseOrderLineItems || []).reduce(
        (acc, item) => {
          const qty = item.orderedQuantity || item.quantity || 0;
          return acc + qty * (item.unitPrice || 0);
        },
        0,
      ); */

      const grandTotal = Number(po.grandTotal) || 0;
      const vat = Number(po.vat) || 0;

      const poPayload = [
        {
          poNumber: approvedPoNumber || "",
          manualPONumber: po.manualPoNumber || null,
          vendorId: po.vendorDetails?.vendorCode || "",
          poDate:
            po.creationDate || po.orderDate || po.poDate
              ? new Date(
                  po.creationDate || po.orderDate || po.poDate,
                ).toISOString()
              : new Date().toISOString(),
          paymentTerms: po.paymentTerms || "",
          currency: po.currency || "PHP",
          status: "Approved", // Generally opens up after approval
          subTotal: grandTotal,
          totalDiscount: 0,
          taxAmount: vat,
          totalAmount: grandTotal,
          reference: po.poNumber || "",
          notes: po.internalNotes || "",
          createdDate: new Date().toISOString(),
          createdBy: currentUserId || 0,
          purchaseOrderDetails: (po.purchaseOrderLineItems || []).map(
            (item) => {
              const qty = item.orderedQuantity || item.quantity || 0;
              const itemSubtotal = qty * (item.unitPrice || 0);
              console.log({item})
              return {
                purchaseOrderHeaderId: po.id || 0,
                itemCode: item.itemCode || "",
                itemDescription: item.productName || "",
                uomName: item.unitOfMeasure || "",
                brandName: item.brandName || "",
                categoryName: item.categoryName || "",
                quantity: Number(qty),
                taxationCodeId: 0,
                unitPrice: Number(item.unitPrice) || 0,
                subTotal: Number(itemSubtotal),
                totalDiscount: 0,
                taxAmount: 0,
                totalAmount: Number(itemSubtotal),
                notes: "",
              };
            },
          ),
        },
      ];

      await axios.post(`${domainAccounting}/api/PurchaseOrder/bulk`, poPayload);
      toast.success(`Successfully pushed ${po.poNumber} to Accounting!`);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to push ${po.poNumber || "order"} to Accounting.`);
    }
  };

  const handleManualSync = async (startDate, endDate) => {
    setIsSyncing(true);
    try {
      // 1. Filter local live POs by Date Range
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      const liveOrdersInRange = poData.filter((po) => {
        if (po.isStaging) return false; // Only sync fully approved/live orders
        const poDate = new Date(po.creationDate);
        return poDate >= start && poDate <= end;
      });

      if (liveOrdersInRange.length === 0) {
        toast.info("No live orders found within the selected date range.");
        setIsSyncing(false);
        return;
      }

      // 2. Fetch already synced records from Accounting API
      const accountingRes = await axios.get(
        `${domainAccounting}/api/PurchaseOrder`,
      );
      const records = Array.isArray(accountingRes.data)
        ? accountingRes.data
        : Array.isArray(accountingRes.data?.items)
          ? accountingRes.data.items
          : [];
      const synced = new Set(records.map((p) => p.poNumber).filter(Boolean));

      // 3. Diff and Find unsynced
      const unsyncedLivePOs = liveOrdersInRange.filter(
        (po) => po.poNumber && !synced.has(po.poNumber),
      );

      if (unsyncedLivePOs.length === 0) {
        toast.info(
          "All live orders in this date range are already synced to Accounting.",
        );
        setIsSyncing(false);
        return;
      }

      // 4. Bulk Push Unsynced POs and GRs
      let syncedCount = 0;
      for (const po of unsyncedLivePOs) {
        /* const currentSubtotal = po.purchaseOrderLineItems.reduce(
          (acc, item) => {
            const qty = item.orderedQuantity || item.quantity || 0;
            return acc + qty * (item.unitPrice || 0);
          },
          0,
        ); */

        const poPayload = [
          {
            poNumber: po.poNumber || "",
            vendorId: po.vendorDetails?.vendorCode || po.vendor || "",
            poDate: po.creationDate
              ? new Date(po.creationDate).toISOString()
              : new Date().toISOString(),
            paymentTerms: po.paymentTerms || "",
            currency: po.currency || "PHP",
            status: po.status || "",
            subTotal: Number(po.grandTotal) || 0,
            totalDiscount: 0,
            taxAmount: Number(po.vat) || 0,
            totalAmount: Number(po.grandTotal) || 0,
            reference: po.poNumber || "",
            notes: po.internalNotes || "",
            createdDate: new Date().toISOString(),
            createdBy: currentUserId || 0,
            purchaseOrderDetails: po.purchaseOrderLineItems.map((item) => {
              const qty = item.orderedQuantity || item.quantity || 0;
              const itemSubtotal = qty * (item.unitPrice || 0);

              return {
                purchaseOrderHeaderId: po.id || 0,
                itemCode: item.productName || item.productId?.toString() || "",
                quantity: Number(qty),
                taxationCodeId: 0,
                unitPrice: Number(item.unitPrice) || 0,
                subTotal: Number(itemSubtotal),
                totalDiscount: 0,
                taxAmount: 0,
                totalAmount: Number(itemSubtotal),
                notes: "",
              };
            }),
          },
        ];

        await axios.post(
          `${domainAccounting}/api/PurchaseOrder/bulk`,
          poPayload,
        );

        // Fetch and Sync Goods Receipts (if any exist)
        let receipts = [];
        try {
          const grResponse = await axios.get(
            `${domain}/api/GoodsReceipts/byPo/${po.id}`,
          );
          receipts = grResponse.data || [];
        } catch (grError) {
          console.warn(`Failed to fetch GRs for PO ${po.id}`, grError);
        }

        if (receipts.length > 0) {
          const grPayload = receipts.map((gr) => {
            let totalAmount = 0;
            const goodReceiptDetails = (gr.lines || []).map((line) => {
              const poLine =
                po.purchaseOrderLineItems.find(
                  (pItem) =>
                    pItem.productId === line.productId ||
                    pItem.productName === line.product?.productName,
                ) || {};

              const unitPrice = Number(poLine.unitPrice || 0);
              const orderedQty = Number(
                poLine.orderedQuantity || poLine.quantity || 0,
              );
              const receivedQty = Number(line.quantityReceived || 0);
              const remainingQty = Math.max(0, orderedQty - receivedQty);
              const subTotal = receivedQty * unitPrice;
              totalAmount += subTotal;

              return {
                id: 0,
                goodsReceiptHeaderId: 0,
                itemCode:
                  line.product?.productName ||
                  line.productId?.toString() ||
                  "Unknown",
                orderedQty: orderedQty,
                receivedQty: receivedQty,
                remainingQty: remainingQty,
                unitPrice: unitPrice,
                subTotal: subTotal,
                taxationCodeId: 0,
                totalDiscount: 0,
                taxAmount: 0,
                totalAmount: subTotal,
              };
            });

            return {
              grNumber: gr.receiptNumber || `GR-${gr.id}`,
              purchaseOrderPONumber: po.poNumber || "",
              amount: totalAmount,
              grDate: gr.receiptDate
                ? new Date(gr.receiptDate).toISOString()
                : new Date().toISOString(),
              uploadedFile: "",
              notes: gr.notes || "",
              status: "Closed",
              createdDate: gr.receiptDate
                ? new Date(gr.receiptDate).toISOString()
                : new Date().toISOString(),
              createdBy: currentUserId || 0,
              goodReceiptDetails: goodReceiptDetails,
            };
          });

          await axios.post(
            `${domainAccounting}/api/GoodReceipt/bulk`,
            grPayload,
          );
        }
        syncedCount++;
      }

      toast.success(
        `Successfully synced ${syncedCount} order(s) to Accounting!`,
      );
      setIsSyncModalOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while syncing to Accounting.");
    } finally {
      setIsSyncing(false);
    }
  };

  // ── Modals & Deletions ─────────────────────────────────────
  const openRejectModal = (item, headerId) => {
    setItemToReject({ lineItem: item, headerId });
    setIsRejectModalOpen(true);
  };
  const closeRejectModal = () => {
    setIsRejectModalOpen(false);
    setItemToReject({ lineItem: null, headerId: null });
  };

  const handleRejectSubmit = async (totalRejectedQty, reason, image) => {
    if (!itemToReject.lineItem || !itemToReject.headerId) return;
    setLoading(true);
    try {
      await axios.post(
        `${domain}/api/PurchaseOrderHeaders/${itemToReject.headerId}/lineitems/${itemToReject.lineItem.id}/reject`,
        {
          TotalRejectedQuantity: parseInt(totalRejectedQty),
          ReasonDescription: reason || "",
          ReasonImage: image || null,
        },
      );
      toast.success("Rejection status updated successfully!");
      closeRejectModal();
      fetchData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update rejection.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSoftDelete = async (id, isStaging) => {
    const targetPO = poData.find((p) => p.id === id);
    if (targetPO) {
      const s = targetPO.status?.toLowerCase() || "";
      if (s === "closed") {
        toast.error("Closed purchase orders cannot be deleted.");
        return;
      }
      if (s.includes("partially")) {
        toast.error("Partially received purchase orders cannot be deleted.");
        return;
      }
    }
    if (
      !window.confirm(
        "Archive this purchase order? It can be restored later from the History tab.",
      )
    )
      return;
    try {
      const endpoint = isStaging
        ? `${domain}/api/PurchaseOrderStaging/soft-delete/${id}`
        : `${domain}/api/PurchaseOrderHeaders/soft-delete/${id}`;
      await axios.put(
        endpoint,
        { userID: currentUserId },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Purchase Order archived.");
      if (expandedPO === id) setExpandedPO(null);
      fetchData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to archive purchase order.",
      );
    }
  };

  // ── Filtering ──────────────────────────────────────────────
  const tabFilteredPOs = poData.filter((po) => {
    if (activeTab === "temp") return po.isStaging;
    if (activeTab === "open") return !po.isStaging && po.status !== "Closed";
    if (activeTab === "closed") return !po.isStaging && po.status === "Closed";
    return false;
  });
  const filteredPOs = tabFilteredPOs.filter((po) => {
    const matchesSearch =
      po.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (po.vendor || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (po.vendorDetails?.vendorName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesLocation =
      selectedLocation === "All" ||
      po.location === selectedLocation ||
      selectedLocation === "";
    return matchesSearch && matchesLocation;
  });

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentPOs = filteredPOs.slice(indexOfFirst, indexOfLast);

  const openModal = (po = null) => {
    setPoToEdit(po);
    setIsModalVisible(true);
  };
  const closeModal = () => {
    setIsModalVisible(false);
    setPoToEdit(null);
  };
  const toggleExpandPO = (poId) =>
    setExpandedPO(expandedPO === poId ? null : poId);
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };
  const openReceiptsModal = (po) => {
    setSelectedPoForReceipts(po);
    setIsReceiptsModalOpen(true);
  };

  const draftCount = poData.filter((p) => p.isStaging).length;
  const openCount = poData.filter(
    (p) => !p.isStaging && p.status !== "Closed",
  ).length;
  const closedCount = poData.filter(
    (p) => !p.isStaging && p.status === "Closed",
  ).length;

  const [showSyncToAccountingForm, setShowSyncToAccountingForm] = useState(false);
  const openSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(true);
  }
  const closeSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(false);
  }

  const handleSyncToAccounting = async (dateFrom, dateTo) => {
    if(poData.length <= 0){
      toast.error(`No Orders to Sync.`);
      return
    }

    setLoading(true)
    try {
      const getDateOnly = value => {
        const d = new Date(value);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
      };

      const filtered = poData.filter(i => {
        const date = getDateOnly(i.creationDate);
        const from = getDateOnly(dateFrom);
        const to = getDateOnly(dateTo);

        return date >= from && date <= to;
      });

      // const poPayload = filtered?.filter(i => i.creationDate >= dateFrom && i.creationDate <= dateTo && !i.isStaging).map(po => {
      const poPayload = filtered?.filter(i => !i.isStaging).map(po => {
        const grandTotal = Number(po.grandTotal) || 0;
        const vat = Number(po.vat) || 0;

        return {
          poNumber: po.poNumber || "",
          manualPONumber: po.manualPoNumber || null,
          vendorId: po.vendorDetails?.vendorCode || "",
          poDate:
            po.creationDate || po.orderDate || po.poDate
              ? new Date(
                  po.creationDate || po.orderDate || po.poDate,
                ).toISOString()
              : new Date().toISOString(),
          paymentTerms: po.paymentTerms || "",
          currency: po.currency || "PHP",
          status: po.status, // Generally opens up after approval
          subTotal: grandTotal,
          totalDiscount: 0,
          taxAmount: vat,
          totalAmount: grandTotal,
          reference: po.poNumber || "",
          notes: po.internalNotes || "",
          createdDate: new Date().toISOString(),
          createdBy: currentUserId || 0,
          purchaseOrderDetails: (po.purchaseOrderLineItems || []).map(
            (item) => {
              const qty = item.orderedQuantity || item.quantity || 0;
              const itemSubtotal = qty * (item.unitPrice || 0);
              return {
                purchaseOrderHeaderId: po.id || 0,
                itemCode: item.itemCode || "",
                itemDescription: item.productName || "",
                uomName: item.unitOfMeasure || "",
                brandName: item.brandName || "",
                categoryName: item.categoryName || "",
                quantity: Number(qty),
                taxationCodeId: 0,
                unitPrice: Number(item.unitPrice) || 0,
                subTotal: Number(itemSubtotal),
                totalDiscount: 0,
                taxAmount: 0,
                totalAmount: Number(itemSubtotal),
                notes: "",
              };
            },
          ),
        }
      });

      await axios.post(`${domainAccounting}/api/PurchaseOrder/bulk`, poPayload);
      toast.success(`Successfully pushed ${poPayload.length || 0} to Accounting!`);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to push "order"} to Accounting.`);
    } finally {
      setLoading(false)
    }
  };

  return (
    <div className="min-h-screen pb-12 bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />

      <ManualSyncModal
        isOpen={isSyncModalOpen}
        onClose={() => !isSyncing && setIsSyncModalOpen(false)}
        onSync={handleManualSync}
        isSyncing={isSyncing}
      />

      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-xl">
                <FileText size={22} className="text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Purchase Orders
                </h1>
                <p className="text-gray-500 text-sm mt-0.5">
                  Manage orders, drafts, and approvals
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* New ? Icon for Manual Sync */}
              <button
                onClick={() => setIsSyncModalOpen(true)}
                title="Sync Manually to Accounting"
                className="p-2 text-blue-500 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-full transition shadow-sm border border-blue-200"
              >
                <HelpCircle size={20} />
              </button>

              <button
                onClick={() => openSyncToAccountingForm()}
                className="flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium transition"
              >
                <History size={16} />
                <span className="hidden sm:inline">Sync To Accounting</span>
              </button>

              <button
                onClick={() => setIsHistoryModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100 rounded-xl text-sm font-medium transition"
              >
                <History size={16} />
                <span className="hidden sm:inline">History</span>
              </button>
              <button
                onClick={() => openModal()}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-xl shadow-md shadow-blue-200 transition-all text-sm"
              >
                <Plus size={18} />
                Create PO
              </button>
            </div>
          </div>

          <div className="flex gap-1 mt-5 p-1 bg-gray-100 rounded-xl max-w-max">
            {[
              {
                key: "temp",
                label: "Drafts",
                count: draftCount,
                icon: <FileText size={13} />,
              },
              {
                key: "open",
                label: "Open",
                count: openCount,
                icon: <Clock size={13} />,
              },
              {
                key: "closed",
                label: "Closed",
                count: closedCount,
                icon: <Archive size={13} />,
              },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  activeTab === tab.key
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {tab.icon}
                {tab.label}
                <span
                  className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                    activeTab === tab.key
                      ? "bg-blue-50 text-blue-600"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-5">
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 mb-5">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by PO number or vendor..."
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all text-sm outline-none"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="relative min-w-[180px]">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 appearance-none bg-white text-sm outline-none"
              >
                {locations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc === "All" ? "All Locations" : loc}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <Loader />
          </div>
        ) : (
          <>
            <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full text-sm text-left text-gray-600">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="w-10 px-5 py-3.5"></th>
                    <th className="px-5 py-3.5 font-semibold">PO Number</th>
                    <th className="px-5 py-3.5 font-semibold">Vendor</th>
                    <th className="px-5 py-3.5 font-semibold">Date</th>
                    <th className="px-5 py-3.5 font-semibold">Status</th>
                    <th className="px-5 py-3.5 font-semibold text-right">
                      Amount
                    </th>
                    <th className="px-5 py-3.5 font-semibold text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentPOs.map((po) => (
                    <React.Fragment key={po.id}>
                      <tr
                        onClick={() => toggleExpandPO(po.id)}
                        className={`hover:bg-blue-50/30 transition cursor-pointer ${expandedPO === po.id ? "bg-blue-50/20" : ""}`}
                      >
                        <td className="px-5 py-3.5 text-center">
                          <button className="p-1 hover:bg-gray-200 rounded-lg transition-colors text-gray-400">
                            {expandedPO === po.id ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </button>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900">
                              {po.manualPoNumber || po.poNumber}
                            </span>
                            {po.isStaging && (
                              <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                DRAFT
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-gray-900">
                            {po.vendorDetails?.vendorName || po.vendor}
                          </p>
                          <p className="text-xs text-gray-400">
                            {po.vendorDetails?.vendorCode || ""}
                          </p>
                        </td>
                        <td className="px-5 py-3.5 text-gray-500">
                          {formatDate(po.creationDate)}
                        </td>
                        <td className="px-5 py-3.5">
                          <StatusBadge status={po.status} />
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <PriceDisplay
                            currentTotal={po.grandTotal}
                            originalTotal={calculateOriginalTotal(po)}
                            currency={po.currency}
                          />
                        </td>
                        <td className="px-5 py-3.5">
                          <div
                            className="flex items-center justify-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {po.isStaging ? (
                              <>
                                <POApprovalActions
                                  po={po}
                                  currentUserId={currentUserId}
                                  refreshData={fetchData}
                                  onEdit={openModal}
                                  onDelete={(id) => handleSoftDelete(id, true)}
                                  onApproveSuccess={(approvedPoNumber) => 
                                    pushSingleOrderToAccounting(po, approvedPoNumber)
                                  }
                                />
                                <button
                                  onClick={() => triggerPrint(po)}
                                  title="Print"
                                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                                >
                                  <Printer size={16} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => triggerPrint(po)}
                                  title="Print"
                                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                                >
                                  <Printer size={16} />
                                </button>
                                {po.status !== "Closed" &&
                                  !po.status
                                    ?.toLowerCase()
                                    .includes("partially") && (
                                    <button
                                      onClick={() => openModal(po)}
                                      title="Edit"
                                      className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition"
                                    >
                                      <Edit size={16} />
                                    </button>
                                  )}
                                {po.status !== "Closed" &&
                                  !po.status
                                    ?.toLowerCase()
                                    .includes("partially") && (
                                    <button
                                      onClick={() =>
                                        handleSoftDelete(po.id, false)
                                      }
                                      title="Delete"
                                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* ── Expanded Detail Row ── */}
                      {expandedPO === po.id && (
                        <tr>
                          <td colSpan="7" className="bg-slate-50/70 px-6 py-5">
                            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-6">
                                {[
                                  {
                                    icon: <MapPin size={11} />,
                                    label: "Location",
                                    value: po.location || "N/A",
                                  },
                                  {
                                    icon: <Calendar size={11} />,
                                    label: "Delivery Date",
                                    value: formatDate(po.expectedDeliveryDate),
                                  },
                                  {
                                    icon: <User size={11} />,
                                    label: "Approver",
                                    value: po.approverName || "N/A",
                                  },
                                  {
                                    icon: <CreditCard size={11} />,
                                    label: "Payment Terms",
                                    value: po.paymentTerms || "N/A",
                                  },
                                ].map((info) => (
                                  <div key={info.label} className="space-y-1">
                                    <span className="text-xs text-gray-400 uppercase font-semibold flex items-center gap-1">
                                      {info.icon} {info.label}
                                    </span>
                                    <p className="text-sm font-medium text-gray-800">
                                      {info.value}
                                    </p>
                                  </div>
                                ))}
                                {po.internalNotes && (
                                  <div className="col-span-2 md:col-span-4 bg-amber-50 p-3 rounded-lg border border-amber-100">
                                    <span className="text-xs text-amber-700 font-bold uppercase block mb-1">
                                      Internal Notes
                                    </span>
                                    <p className="text-sm text-amber-900">
                                      {po.internalNotes}
                                    </p>
                                  </div>
                                )}
                              </div>

                              {po.isStaging && po.rejectionReason && (
                                <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-100 flex items-start gap-2">
                                  <ShieldAlert
                                    size={14}
                                    className="text-red-500 mt-0.5 shrink-0"
                                  />
                                  <div>
                                    <span className="text-xs font-bold text-red-600 block mb-0.5">
                                      Rejection Reason
                                    </span>
                                    <p className="text-sm text-red-700">
                                      {po.rejectionReason}
                                    </p>
                                  </div>
                                </div>
                              )}

                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wide flex items-center gap-1.5">
                                  <Package size={14} /> Order Items
                                </h4>
                                <button
                                  onClick={() => openReceiptsModal(po)}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium border border-blue-100 hover:bg-blue-100 transition"
                                >
                                  <Package size={13} /> View Receipts
                                </button>
                              </div>

                              <div className="border border-gray-200 rounded-xl overflow-hidden mb-5">
                                <table className="w-full text-xs text-left text-gray-600">
                                  <thead className="bg-gray-50 text-gray-500 uppercase font-semibold">
                                    <tr>
                                      <th className="px-4 py-2.5">Product</th>
                                      <th className="px-4 py-2.5">Qty</th>
                                      <th className="px-4 py-2.5 text-right">
                                        Unit Price
                                      </th>
                                      {!po.isStaging && (
                                        <>
                                          <th className="px-4 py-2.5 text-emerald-600 text-center">
                                            Received
                                          </th>
                                          <th className="px-4 py-2.5 text-red-500 text-center">
                                            Rejected
                                          </th>
                                        </>
                                      )}
                                      <th className="px-4 py-2.5 text-right text-gray-400">
                                        Original
                                      </th>
                                      <th className="px-4 py-2.5 text-right">
                                        Line Total
                                      </th>
                                      {!po.isStaging && (
                                        <th className="px-4 py-2.5 text-center">
                                          Action
                                        </th>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {po.purchaseOrderLineItems.map((item) => {
                                      const qty =
                                        item.orderedQuantity || item.quantity;
                                      const originalAmount =
                                        qty * item.unitPrice;
                                      return (
                                        <tr
                                          key={item.id}
                                          className="hover:bg-gray-50 transition"
                                        >
                                          <td className="px-4 py-2.5 font-medium text-gray-900">
                                            {item.productName}
                                          </td>
                                          <td className="px-4 py-2.5 text-gray-500">
                                            {qty} {item.unitOfMeasure}
                                          </td>
                                          <td className="px-4 py-2.5 text-right">
                                            {formatCurrency(
                                              item.unitPrice,
                                              po.currency,
                                            )}
                                          </td>
                                          {!po.isStaging && (
                                            <>
                                              <td className="px-4 py-2.5 text-emerald-600 text-center font-bold">
                                                {item.receivedQuantity || 0}
                                              </td>
                                              <td className="px-4 py-2.5 text-red-500 text-center font-bold">
                                                {item.rejectedQuantity || 0}
                                              </td>
                                            </>
                                          )}
                                          <td className="px-4 py-2.5 text-right text-gray-400">
                                            {formatCurrency(
                                              originalAmount,
                                              po.currency,
                                            )}
                                          </td>
                                          <td className="px-4 py-2.5 text-right font-bold text-gray-900">
                                            {item.rejectedQuantity > 0
                                              ? formatCurrency(
                                                  (qty -
                                                    item.rejectedQuantity) *
                                                    item.unitPrice,
                                                  po.currency,
                                                )
                                              : formatCurrency(
                                                  item.lineTotal,
                                                  po.currency,
                                                )}
                                          </td>
                                          {!po.isStaging && (
                                            <td className="px-4 py-2.5 text-center">
                                              <div className="flex items-center justify-center gap-1.5">
                                                {item.rejectedQuantity > 0 && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setItemToView(item);
                                                      setIsViewModalOpen(true);
                                                    }}
                                                    className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition"
                                                    title="View rejection"
                                                  >
                                                    <Eye size={14} />
                                                  </button>
                                                )}
                                                {po.status !== "Closed" && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      openRejectModal(
                                                        item,
                                                        po.id,
                                                      );
                                                    }}
                                                    className="px-2.5 py-1 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition"
                                                  >
                                                    Reject
                                                  </button>
                                                )}
                                              </div>
                                            </td>
                                          )}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>

                              <div className="flex justify-end">
                                <div className="w-full md:w-72 bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2 text-sm">
                                  {[
                                    {
                                      label: "Shipping Cost",
                                      value: formatCurrency(
                                        po.shippingCost,
                                        po.currency,
                                      ),
                                      color: "text-gray-700",
                                    },
                                    {
                                      label: "VAT",
                                      value: formatCurrency(
                                        po.vat,
                                        po.currency,
                                      ),
                                      color: "text-gray-700",
                                    },
                                    {
                                      label: "EWT (Withholding)",
                                      value: `-${formatCurrency(po.ewt, po.currency)}`,
                                      color: "text-red-500",
                                    },
                                  ].map((row) => (
                                    <div
                                      key={row.label}
                                      className="flex justify-between text-gray-500"
                                    >
                                      <span>{row.label}</span>
                                      <span
                                        className={`font-medium ${row.color}`}
                                      >
                                        {row.value}
                                      </span>
                                    </div>
                                  ))}
                                  {po.purchaseOrderLineItems.some(
                                    (i) => i.rejectedQuantity > 0,
                                  ) && (
                                    <div className="flex justify-between text-gray-500">
                                      <span>Rejected Amount</span>
                                      <span className="font-medium text-red-500">
                                        -
                                        {formatCurrency(
                                          po.purchaseOrderLineItems.reduce(
                                            (sum, item) =>
                                              sum +
                                              (item.rejectedQuantity || 0) *
                                                (item.unitPrice || 0),
                                            0,
                                          ),
                                          po.currency,
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-gray-900">
                                    <span>Grand Total</span>
                                    <PriceDisplay
                                      currentTotal={po.grandTotal}
                                      originalTotal={calculateOriginalTotal(po)}
                                      currency={po.currency}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}

                  {currentPOs.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <AlertCircle size={22} className="text-gray-400" />
                          </div>
                          <p className="text-gray-700 font-semibold">
                            No orders found
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            {searchTerm
                              ? "Try a different search term."
                              : `No ${activeTab === "temp" ? "drafts" : activeTab === "closed" ? "closed orders" : "open orders"} available.`}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="lg:hidden space-y-1">
              {currentPOs.map((po) => (
                <MobilePOCard
                  key={po.id}
                  po={po}
                  expanded={expandedPO === po.id}
                  onToggle={toggleExpandPO}
                  onEdit={openModal}
                  onSoftDelete={handleSoftDelete}
                  onRejectItem={openRejectModal}
                  onPrint={triggerPrint}
                  currentUserId={currentUserId}
                  refreshData={fetchData}
                  onApproveSuccess={(approvedPoNumber) => 
                    pushSingleOrderToAccounting(po, approvedPoNumber)
                  }
                />
              ))}
              {currentPOs.length === 0 && (
                <div className="text-center py-14 text-gray-400 bg-white rounded-2xl border border-gray-100">
                  <AlertCircle size={36} className="mx-auto mb-2 opacity-40" />
                  <p className="font-medium text-gray-500">No orders found.</p>
                </div>
              )}
            </div>

            <div className="mt-5">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredPOs.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>
          </>
        )}
      </div>

      <div style={{ display: "none" }}>
        <PrintPurchaseOrder ref={printPORef} po={printData} />
      </div>

      {isModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <AddPurchaseOrder
              onClose={closeModal}
              refreshData={fetchData}
              poToEdit={poToEdit}
            />
          </div>
        </div>
      )}

      <POHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        currentUserId={currentUserId}
        onRestored={fetchData}
      />
      <RejectItemModal
        isOpen={isRejectModalOpen}
        onClose={closeRejectModal}
        onSubmit={handleRejectSubmit}
        lineItem={itemToReject.lineItem}
      />
      <ViewRejectionModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setItemToView(null);
        }}
        lineItem={itemToView}
      />
      <POReceiptsModal
        isOpen={isReceiptsModalOpen}
        onClose={() => {
          setIsReceiptsModalOpen(false);
          setSelectedPoForReceipts(null);
        }}
        poId={selectedPoForReceipts?.id}
        poNumber={selectedPoForReceipts?.poNumber}
      />

      <SyncToAccountingModal 
        isOpen={showSyncToAccountingForm}
        onClose={closeSyncToAccountingForm}
        onConfirm={handleSyncToAccounting}
      />
    </div>
  );
};

const SyncToAccountingModal = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onConfirm(dateFrom, dateTo);
    onClose();
  };
  
  const inputClass =
    "block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900 transition-all focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 outline-none";
  const labelClass =
    "mb-1 ml-0.5 block text-[10px] font-black uppercase tracking-widest text-gray-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
        {/* Header */}
        <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
          <h2 className="text-white text-lg font-bold flex items-center gap-2">
            Sync To Accounting
          </h2>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            You are about to sync data to accounting.
            <br />
            <br />
            <strong>Please fill up fields to filter data</strong>
          </p>

          <div className="flex flex-col gap-3">
            <div>
              <label className={labelClass}>
                Order Date From
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className={`${inputClass} !border-indigo-300`}
              />
            </div>
            <div>
              <label className={labelClass}>
                Order Date To
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className={`${inputClass} !border-indigo-300`}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-md"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllPurchaseOrders;
